import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { LayoutDashboard, FileText, BookOpen, GraduationCap, Bookmark, User, LogOut, Menu, X, Shield, Home } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/PublicNav";
import { TIERS } from "@/lib/constants";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/blog", label: "The Desk", icon: FileText },
  { to: "/playbooks", label: "Playbooks", icon: BookOpen },
  { to: "/courses/ultimate-sales-mastery", label: "The Course", icon: GraduationCap },
  { to: "/saved", label: "Saved", icon: Bookmark },
  { to: "/profile", label: "Profile", icon: User },
];

export default function DashboardLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const tier = TIERS[user?.tier] || TIERS.free;

  const SidebarInner = () => (
    <div className="flex h-full flex-col">
      <div className="px-6 py-5"><Logo light /></div>
      <nav className="flex-1 space-y-1 px-3">
        {NAV.map((n) => {
          const active = pathname === n.to || (n.to !== "/dashboard" && pathname.startsWith(n.to));
          return (
            <Link key={n.to} to={n.to} onClick={() => setOpen(false)} data-testid={`side-${n.label.toLowerCase()}`}
              className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
                active ? "bg-white/10 text-white" : "text-slate-400 hover:bg-white/5 hover:text-white"}`}>
              <n.icon className="h-[18px] w-[18px]" strokeWidth={1.75} /> {n.label}
            </Link>
          );
        })}
        {user?.role === "admin" && (
          <Link to="/admin" onClick={() => setOpen(false)} data-testid="side-admin"
            className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-semibold transition-colors ${
              pathname.startsWith("/admin") ? "bg-emerald text-white" : "text-emerald hover:bg-white/5"}`}>
            <Shield className="h-[18px] w-[18px]" strokeWidth={1.75} /> Admin
          </Link>
        )}
      </nav>
      <div className="border-t border-white/10 p-3">
        <div className="mb-2 rounded-md bg-white/5 px-3 py-2.5">
          <p className="truncate text-sm font-semibold text-white">{user?.name}</p>
          <span className={`mt-1 inline-block rounded px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tier.color}`}>{tier.label}</span>
        </div>
        <button onClick={() => navigate("/")} className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-400 hover:text-white" data-testid="side-home">
          <Home className="h-[18px] w-[18px]" strokeWidth={1.75} /> Public site
        </button>
        <button onClick={logout} data-testid="side-logout"
          className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold text-slate-400 hover:text-white">
          <LogOut className="h-[18px] w-[18px]" strokeWidth={1.75} /> Log out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-offwhite">
      <aside className="fixed inset-y-0 left-0 hidden w-64 bg-navy lg:block">{SidebarInner()}</aside>
      <div className="flex items-center justify-between border-b border-slate-200 bg-navy px-4 py-3 lg:hidden">
        <Logo light />
        <button onClick={() => setOpen(true)} data-testid="dash-mobile-toggle" aria-label="Open menu"><Menu className="h-6 w-6 text-white" /></button>
      </div>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-navy">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 text-white" data-testid="dash-mobile-close"><X className="h-6 w-6" /></button>
            {SidebarInner()}
          </div>
        </div>
      )}
      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 py-8 lg:px-10 lg:py-10">{children}</div>
      </main>
    </div>
  );
}
